import React, { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  Rating,
  Stack,
  Typography,
} from '@mui/material';
import { EyeIcon, EyeOffIcon } from 'lucide-react';

import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import {
  DELETE_CMA_USER_REVIEW,
  INSERT_CMA_USER_REVIEW,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import type { BrokerReview } from '@modules/reports/cma-reports/step-agent/types';
import type { CMAPageId } from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';

export type ClientReviewCardProps = {
  review: BrokerReview;
  locked: boolean;
  onPendingChange: (reviewId: string | null) => void;
};

export const ClientReviewCard = ({
  review,
  locked,
  onPendingChange,
}: ClientReviewCardProps) => {
  const { t, locale } = useLocale();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const { data: cmaReport } = useReportContext<CMAPageId>();

  const isVisible = review.is_visible;

  const reviewVisibilityMutationOptions = {
    onError: setErrorLike,
  };

  const [deleteReview, { loading: deleteLoading }] = useMutation(
    DELETE_CMA_USER_REVIEW,
    reviewVisibilityMutationOptions,
  );

  const [insertReview, { loading: insertLoading }] = useMutation(
    INSERT_CMA_USER_REVIEW,
    reviewVisibilityMutationOptions,
  );

  const isLoading = deleteLoading || insertLoading;

  const reviewerName =
    review.display_name ||
    [review.first_name, review.last_name].filter(Boolean).join(' ') ||
    review.user?.first_name ||
    t('Anonymous');

  const handleVisibilityToggle = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!cmaReport?.id) {
      return;
    }

    const options = {
      variables: {
        cmaReportId: cmaReport.id,
        reviewId: review.id,
      },
    };

    onPendingChange(review.id);
    try {
      if (isVisible) {
        await deleteReview(options);
      } else {
        await insertReview(options);
      }
    } finally {
      onPendingChange(null);
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        transition: 'all 0.2s ease',
        opacity: isVisible ? 1 : 0.5,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Stack sx={{ p: 2, gap: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {reviewerName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  px: 1,
                  py: 0.25,
                  backgroundColor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                {review.is_seller ? t('Seller') : t('Buyer')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Rating value={review.rating ?? 0} readOnly size="small" />
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                }}
              >
                {review.created_at &&
                  toHumanReadableDate(locale, review.created_at)}
              </Typography>
            </Box>

            {review.google_address?.formatted_address && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  mb: 1,
                }}
              >
                {review.google_address.formatted_address}
              </Typography>
            )}
          </Box>

          <IconButton
            onClick={handleVisibilityToggle}
            size="small"
            disabled={isLoading || locked}
            sx={{ color: 'text.secondary' }}
          >
            {isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isVisible ? (
              <EyeIcon size={20} />
            ) : (
              <EyeOffIcon size={20} />
            )}
          </IconButton>
        </Box>

        {review.review_text && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.5,
            }}
          >
            {review.review_text}
          </Typography>
        )}
      </Stack>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </Card>
  );
};
